/**
 * Taxonomy Link Component
 *
 * Navigation link with hover states and underline animation.
 * Figma: Taxonomy Hover Link (Node: 1:2846)
 *
 * States: Active, Hover, Inactive
 * Sizes: Default (12.25px), Small (10.5px)
 * Colors:
 * - Active/Hover: #000000 (text.black) with Inter Medium
 * - Inactive: #6A7282 (text.headerMdGray) with Inter Regular
 * Underline: 1.739px height (default), 1.2px (small)
 */
"use client";

import { useState } from "react";

import { colors } from "@/styles/design-tokens";

interface TaxonomyLinkProps {
  label: string;
  active?: boolean;
  size?: "default" | "small";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  expanded?: boolean;
  controls?: string;
  className?: string;
}

export function TaxonomyLink({
  label,
  active = false,
  size = "default",
  onClick,
  expanded,
  controls,
  className = "",
}: TaxonomyLinkProps) {
  const [isHovered, setIsHovered] = useState(false);

  const isActiveOrHovered = active || isHovered;
  const underlineHeight = size === "default" ? "1.739px" : "1.2px";
  const paddingBottom = size === "default" ? "1.739px" : "0.8px";

  // Typography class based on state and size
  const textClass =
    size === "default"
      ? isActiveOrHovered
        ? "text-action text-ink"
        : "text-action-muted text-ink-muted"
      : isActiveOrHovered
        ? "text-action-sm text-ink"
        : "text-action-sm-muted text-ink-muted";

  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={controls}
      data-testid="taxonomy-root-trigger"
      data-root-name={label}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`inline-flex flex-col items-start ${className}`}
    >
      {/* Link Container with padding */}
      <div className="flex flex-col items-start" style={{ paddingBottom }}>
        <span className={textClass}>{label}</span>

        {/* Underline - visible when active, uses negative margin to overlap padding */}
        {active && (
          <div
            style={{
              backgroundColor: colors.text.black,
              height: underlineHeight,
              width: "100%",
              marginBottom: `-${underlineHeight}`,
            }}
          />
        )}
      </div>
    </button>
  );
}
